import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

import { formatFileSize } from '../../components/workflowShared';
import type { AppChatMessage, AppIssueRef, AppWorkspaceResourcePointer } from './appChatStore';
import { IconArchive, IconArrowUp, IconCheck, IconClose, IconCopy, IconFile, IconPlus, IconStop } from './appIcons';
import { AppMarkdown } from './AppMarkdown';

const COMPOSER_MAX_HEIGHT_PX = 220;
// Chat API replies are usually quick, so they only get a label and timer once they are slow.
const REPLY_SLOW_AFTER_S = 8;
const REPLY_STILL_WORKING_AFTER_S = 30;
const COPY_FEEDBACK_MS = 2000;

export interface AppChatSuggestion {
  label: string;
  icon: ReactNode;
  prompt: string;
  /** 'send' sends the prompt right away; 'fill' puts it in the composer for the user to complete. */
  mode: 'send' | 'fill';
  /** Runs instead of the prompt, for suggestions that open another view. */
  onSelect?: () => void;
}

interface AppZipAttachment {
  name: string;
  size: number;
}

interface AppChatViewProps {
  /** Identifies the thread; switching it resets the draft and scroll position. */
  threadKey: string;
  messages: AppChatMessage[];
  isReplying: boolean;
  /** The pending reply was routed to Codex, which is slower and gets its own label. */
  isCodexReplying?: boolean;
  placeholder: string;
  greeting?: string;
  /** Shown above the greeting while the chat is empty. */
  hero?: ReactNode;
  suggestions?: AppChatSuggestion[];
  renderWidget?: (message: AppChatMessage) => ReactNode;
  /** Issues attached to the next message, shown as chips in the composer. */
  attachments?: AppIssueRef[];
  resources?: AppWorkspaceResourcePointer[];
  resourceAttachments?: AppWorkspaceResourcePointer[];
  zipAttachment?: AppZipAttachment;
  zipError?: string;
  onRemoveAttachment?: (key: string) => void;
  onAttachResource?: (resourceId: string) => void;
  onRemoveResource?: (resourceId: string) => void;
  onOpenResource?: (resourceId: string) => void;
  onSelectZip?: (file: File | null) => void;
  onRemoveZip?: () => void;
  onOpenIssue?: (key: string) => void;
  /** Returns false when the message could not be sent, which keeps the draft. */
  onSend: (content: string) => boolean | Promise<boolean>;
  onStop?: () => void;
}

function AppIssueChip({
  issueRef,
  onOpen,
  onRemove
}: {
  issueRef: AppIssueRef;
  onOpen?: () => void;
  onRemove?: () => void;
}) {
  return (
    <span className="app-issue-chip">
      <button type="button" className="app-issue-chip-main" title={issueRef.title} onClick={onOpen}>
        <span className={`app-sev-dot sev-${issueRef.severity}`} aria-hidden={true} />
        <span>{issueRef.title}</span>
      </button>
      {onRemove && (
        <button type="button" className="app-issue-chip-remove" aria-label={`Remove ${issueRef.title}`} onClick={onRemove}>
          <IconClose aria-hidden={true} />
        </button>
      )}
    </span>
  );
}

/** Copies the message's raw text. Stays hidden until the message is hovered, and shows the result briefly. */
function AppMessageCopy({ text }: { text: string }) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const label = status === 'copied' ? 'Copied' : status === 'failed' ? 'Could not copy' : 'Copy message';

  useEffect(() => {
    if (status === 'idle') {
      return undefined;
    }

    const timer = window.setTimeout(() => setStatus('idle'), COPY_FEEDBACK_MS);

    return () => window.clearTimeout(timer);
  }, [status]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus('copied');
    } catch {
      setStatus('failed');
    }
  };

  return (
    <button
      type="button"
      className={`app-message-copy${status === 'idle' ? '' : ' is-active'}`}
      aria-label={label}
      title={label}
      onClick={() => void copy()}
    >
      {status === 'copied' ? <IconCheck aria-hidden={true} /> : status === 'failed' ? <IconClose aria-hidden={true} /> : <IconCopy aria-hidden={true} />}
    </button>
  );
}

function AppAssistantMessage({ children }: { children: ReactNode }) {
  return (
    <div className="app-message assistant">
      <span className="app-message-avatar" aria-hidden={true}>
        <img src="/logoinvert.png" alt="" />
      </span>
      <div className="app-message-body">{children}</div>
    </div>
  );
}

const formatElapsed = (seconds: number): string => {
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

/** Typing dots, plus a short label and timer when the reply goes to Codex or takes a while. */
function AppReplyIndicator({ isCodex }: { isCodex: boolean }) {
  const [startedMs] = useState(() => Date.now());
  const [nowMs, setNowMs] = useState(startedMs);
  const elapsed = Math.floor((nowMs - startedMs) / 1000);
  const isSlow = elapsed >= REPLY_SLOW_AFTER_S;
  const isStillWorking = elapsed >= REPLY_STILL_WORKING_AFTER_S;
  const label = isCodex
    ? isStillWorking ? 'Daybreak is still working' : 'Using Daybreak'
    : isStillWorking ? 'Still thinking' : isSlow ? 'Thinking' : '';

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <span className="app-reply-status" role="status" aria-label={label || 'Havu is replying'}>
      <span className="app-typing" aria-hidden={true}>
        <span />
        <span />
        <span />
      </span>
      {label && <span className="app-reply-status-label" aria-hidden={true}>{label}</span>}
      {isSlow && <span className="app-reply-status-time" aria-hidden={true}>{formatElapsed(elapsed)}</span>}
    </span>
  );
}

function AppResourceChip({
  resource,
  onOpen,
  onRemove
}: {
  resource?: AppWorkspaceResourcePointer;
  onOpen?: () => void;
  onRemove?: () => void;
}) {
  const name = resource?.name ?? 'Unavailable resource';

  return (
    <span className={`app-issue-chip app-resource-chip${onOpen ? ' openable' : ''}`} title={name}>
      {onOpen ? (
        <button type="button" className="app-issue-chip-main" onClick={onOpen}>
          <IconFile aria-hidden={true} />
          <span>{name}</span>
        </button>
      ) : (
        <span className="app-issue-chip-main">
          <IconFile aria-hidden={true} />
          <span>{name}</span>
        </span>
      )}
      {onRemove && (
        <button type="button" className="app-issue-chip-remove" aria-label={`Remove ${name}`} onClick={onRemove}>
          <IconClose aria-hidden={true} />
        </button>
      )}
    </span>
  );
}

function AppComposer({
  draft,
  textareaRef,
  placeholder,
  isReplying,
  attachments,
  resources,
  resourceAttachments,
  zipAttachment,
  zipError,
  onRemoveAttachment,
  onAttachResource,
  onRemoveResource,
  onSelectZip,
  onRemoveZip,
  onOpenIssue,
  onDraftChange,
  onSubmit,
  onStop
}: {
  draft: string;
  textareaRef: RefObject<HTMLTextAreaElement>;
  placeholder: string;
  isReplying: boolean;
  attachments?: AppIssueRef[];
  resources?: AppWorkspaceResourcePointer[];
  resourceAttachments?: AppWorkspaceResourcePointer[];
  zipAttachment?: AppZipAttachment;
  zipError?: string;
  onRemoveAttachment?: (key: string) => void;
  onAttachResource?: (resourceId: string) => void;
  onRemoveResource?: (resourceId: string) => void;
  onSelectZip?: (file: File | null) => void;
  onRemoveZip?: () => void;
  onOpenIssue?: (key: string) => void;
  onDraftChange: (draft: string) => void;
  onSubmit: () => void;
  onStop?: () => void;
}) {
  const [isResourceMenuOpen, setIsResourceMenuOpen] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resourcePickerRef = useRef<HTMLDivElement>(null);
  const resourceMenuRef = useRef<HTMLDivElement>(null);
  const canSend = (draft.trim().length > 0 || Boolean(zipAttachment)) && !isReplying;
  const attachedResourceIds = new Set((resourceAttachments ?? []).map((resource) => resource.id));
  const availableResources = onAttachResource
    ? (resources ?? []).filter((resource) => {
      return resource.source !== 'upload' && !attachedResourceIds.has(resource.id);
    })
    : [];

  useEffect(() => {
    if (isReplying) {
      setIsResourceMenuOpen(false);
    }
  }, [isReplying]);

  // The attach tray closes on a click outside it or on Escape.
  useEffect(() => {
    if (!isResourceMenuOpen) {
      return undefined;
    }

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;

      if (!resourcePickerRef.current?.contains(target) && !resourceMenuRef.current?.contains(target)) {
        setIsResourceMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsResourceMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isResourceMenuOpen]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft, textareaRef]);

  return (
    <form
      className={`app-composer${isDraggingFile ? ' is-dragging' : ''}`}
      onSubmit={(event) => {
        event.preventDefault();

        if (canSend) {
          onSubmit();
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          textareaRef.current?.focus();
        }
      }}
      onDragEnter={(event) => {
        if (onSelectZip && event.dataTransfer.types.includes('Files')) {
          event.preventDefault();
          setIsDraggingFile(true);
        }
      }}
      onDragOver={(event) => {
        if (onSelectZip && event.dataTransfer.types.includes('Files')) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsDraggingFile(false);
        }
      }}
      onDrop={(event) => {
        if (!onSelectZip) return;

        event.preventDefault();
        setIsDraggingFile(false);
        onSelectZip(event.dataTransfer.files?.[0] ?? null);
      }}
    >
      {((attachments && attachments.length > 0)
        || (resourceAttachments && resourceAttachments.length > 0)
        || zipAttachment) && (
        <div className="app-composer-refs">
          {zipAttachment && (
            <span className="app-file-card" title={zipAttachment.name}>
              <span className="app-file-card-icon" aria-hidden={true}>
                <IconArchive aria-hidden={true} />
              </span>
              <span className="app-file-card-copy">
                <span className="app-file-card-name">{zipAttachment.name}</span>
                <small>ZIP · {formatFileSize(zipAttachment.size)}</small>
              </span>
              {onRemoveZip && (
                <button type="button" className="app-file-card-remove" aria-label={`Remove ${zipAttachment.name}`} onClick={onRemoveZip}>
                  <IconClose aria-hidden={true} />
                </button>
              )}
            </span>
          )}
          {(attachments ?? []).map((attachment) => (
            <AppIssueChip
              key={attachment.key}
              issueRef={attachment}
              onOpen={onOpenIssue ? () => onOpenIssue(attachment.key) : undefined}
              onRemove={onRemoveAttachment ? () => onRemoveAttachment(attachment.key) : undefined}
            />
          ))}
          {(resourceAttachments ?? []).map((resource) => (
            <AppResourceChip
              key={resource.id}
              resource={resource}
              onRemove={onRemoveResource ? () => onRemoveResource(resource.id) : undefined}
            />
          ))}
        </div>
      )}
      {zipAttachment && <p className="app-upload-note">ZIP attachments can only be used to run a security assessment.</p>}
      {zipError && <p className="app-widget-error app-upload-error" role="alert">{zipError}</p>}
      <div className="app-composer-row">
        {(onSelectZip || (resources && resources.length > 0 && onAttachResource)) && (
          <div className="app-resource-picker" ref={resourcePickerRef}>
            <button
              type="button"
              className="app-attach-resource"
              aria-label="Add files or workspace resources"
              aria-expanded={isResourceMenuOpen}
              title="Add files or workspace resources"
              onClick={() => setIsResourceMenuOpen((current) => !current)}
            >
              <IconPlus aria-hidden={true} />
            </button>
            {onSelectZip && (
              <input
                ref={fileInputRef}
                className="app-upload-input"
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                tabIndex={-1}
                onChange={(event) => {
                  onSelectZip(event.target.files?.[0] ?? null);
                  event.target.value = '';
                }}
              />
            )}
          </div>
        )}
        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();

              if (canSend) {
                onSubmit();
              }
            }
          }}
        />
        <button
          type={isReplying ? 'button' : 'submit'}
          className={`app-send${isReplying ? ' replying' : ''}`}
          disabled={isReplying ? !onStop : !canSend}
          aria-label={isReplying ? 'Stop reply' : 'Send message'}
          onClick={isReplying ? onStop : undefined}
        >
          {isReplying ? <IconStop aria-hidden={true} /> : <IconArrowUp aria-hidden={true} />}
        </button>
      </div>
      {isResourceMenuOpen && (
        <div className="app-resource-menu" ref={resourceMenuRef} role="menu" aria-label="Workspace resources">
          {onSelectZip && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                fileInputRef.current?.click();
                setIsResourceMenuOpen(false);
              }}
            >
              <IconArchive aria-hidden={true} />
              <span>Upload codebase</span>
              <small>ZIP</small>
            </button>
          )}
          {availableResources.map((resource) => (
            <button
              key={resource.id}
              type="button"
              role="menuitem"
              onClick={() => {
                onAttachResource?.(resource.id);
                setIsResourceMenuOpen(false);
              }}
            >
              <IconFile aria-hidden={true} />
              <span>{resource.name}</span>
              <small>{resource.kind}</small>
            </button>
          ))}
          {!onSelectZip && availableResources.length === 0 && <span>All resources are attached</span>}
        </div>
      )}
    </form>
  );
}

function AppChatView({
  threadKey,
  messages,
  isReplying,
  isCodexReplying = false,
  placeholder,
  greeting,
  hero,
  suggestions,
  renderWidget,
  attachments,
  resources = [],
  resourceAttachments = [],
  zipAttachment,
  zipError,
  onRemoveAttachment,
  onAttachResource,
  onRemoveResource,
  onOpenResource,
  onSelectZip,
  onRemoveZip,
  onOpenIssue,
  onSend,
  onStop
}: AppChatViewProps) {
  const [draft, setDraft] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isEmpty = messages.length === 0;
  const resourcesById = useMemo(() => {
    return new Map(resources.map((resource) => [resource.id, resource]));
  }, [resources]);

  useEffect(() => {
    setDraft('');
    textareaRef.current?.focus();
  }, [threadKey]);

  useEffect(() => {
    const thread = threadRef.current;

    thread?.scrollTo({ top: thread.scrollHeight, behavior: messages.length > 0 ? 'smooth' : 'auto' });
  }, [messages.length, isReplying, threadKey]);

  const submit = async () => {
    if (await onSend(draft.trim())) {
      setDraft('');
    }
  };

  const applySuggestion = (suggestion: AppChatSuggestion) => {
    if (suggestion.onSelect) {
      suggestion.onSelect();
      return;
    }

    if (suggestion.mode === 'send') {
      onSend(suggestion.prompt);
      return;
    }

    setDraft(suggestion.prompt);
    window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;

      textarea?.focus();
      textarea?.setSelectionRange(suggestion.prompt.length, suggestion.prompt.length);
    });
  };

  return (
    <div className={`app-chat${isEmpty ? ' is-empty' : ''}`}>
      {!isEmpty && (
        <div className="app-thread" ref={threadRef}>
          <div className="app-thread-inner">
            {messages.map((message) => message.role === 'user' ? (
              <div key={message.id} className="app-message user">
                {((message.issueRefs && message.issueRefs.length > 0) || message.resourceIds.length > 0) && (
                  <div className="app-message-refs">
                    {(message.issueRefs ?? []).map((issueRef) => (
                      <AppIssueChip
                        key={issueRef.key}
                        issueRef={issueRef}
                        onOpen={onOpenIssue ? () => onOpenIssue(issueRef.key) : undefined}
                      />
                    ))}
                    {message.resourceIds.map((resourceId) => (
                      <AppResourceChip
                        key={resourceId}
                        resource={resourcesById.get(resourceId)}
                        onOpen={onOpenResource ? () => onOpenResource(resourceId) : undefined}
                      />
                    ))}
                  </div>
                )}
                <div className="app-message-body">{message.content}</div>
                {message.content && <AppMessageCopy text={message.content} />}
              </div>
            ) : (
              <AppAssistantMessage key={message.id}>
                {message.resourceIds.length > 0 && (
                  <div className="app-assistant-resources">
                    {message.resourceIds.map((resourceId) => (
                      <AppResourceChip
                        key={resourceId}
                        resource={resourcesById.get(resourceId)}
                        onOpen={onOpenResource ? () => onOpenResource(resourceId) : undefined}
                      />
                    ))}
                  </div>
                )}
                {message.content && <AppMarkdown text={message.content} />}
                {message.isCodex && (
                  <span className="app-status-pill app-message-tag" title="Answered by Codex with codebase access">
                    Codex
                  </span>
                )}
                {message.widget && renderWidget?.(message)}
                {message.content && <AppMessageCopy text={message.content} />}
              </AppAssistantMessage>
            ))}
            {isReplying && (
              <AppAssistantMessage>
                <AppReplyIndicator isCodex={isCodexReplying} />
              </AppAssistantMessage>
            )}
          </div>
        </div>
      )}

      <div className="app-dock">
        {isEmpty && hero}
        {isEmpty && greeting && <h1 className="app-greeting">{greeting}</h1>}
        <AppComposer
          draft={draft}
          textareaRef={textareaRef}
          placeholder={placeholder}
          isReplying={isReplying}
          attachments={attachments}
          resources={resources}
          resourceAttachments={resourceAttachments}
          zipAttachment={zipAttachment}
          zipError={zipError}
          onRemoveAttachment={onRemoveAttachment}
          onAttachResource={onAttachResource}
          onRemoveResource={onRemoveResource}
          onSelectZip={onSelectZip}
          onRemoveZip={onRemoveZip}
          onOpenIssue={onOpenIssue}
          onDraftChange={setDraft}
          onSubmit={() => void submit()}
          onStop={onStop}
        />
        {isEmpty && suggestions && suggestions.length > 0 && (
          <div className="app-suggestions">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.label}
                type="button"
                className="app-suggestion"
                onClick={() => applySuggestion(suggestion)}
              >
                {suggestion.icon}
                <span>{suggestion.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AppChatView;
